import { GeckoMeasurement } from './geckoMeasurement'
import { GeckoMeasurementUnit } from './geckoMeasurementUnit'

// TODO: Document this class
export class GeckoColorScaleThreshold {
  //----------------------------------------------------------------------------

  /** The min of this color scale threshold */
  public readonly min: GeckoMeasurement

  /** The mid of this color scale threshold */
  public readonly mid: GeckoMeasurement

  /** The max of this color scale threshold */
  public readonly max: GeckoMeasurement

  /**
   * A simple constructor- private to encourage use of more descriptive static
   * factory methods like CreateFromValue()
   * @param min The numeric value of the min
   * @param mid The numeric value of the mid
   * @param max The numeric value of the max
   */
  private constructor(
    min: GeckoMeasurement,
    mid: GeckoMeasurement,
    max: GeckoMeasurement
  ) {
    const [trueMin, trueMid, trueMax] = [min, mid, max]
      .map((measurement) => measurement.convertTo(min.unit))
      .toSorted((a, b) => a.num - b.num)
    this.min = trueMin
    this.mid = trueMid
    this.max = trueMax
  }

  /**
   * An accessor method for this color scale's measurement unit (an accessor
   * rather than a variable since this is already stored on min, mid, and max)
   */
  public get unit(): GeckoMeasurementUnit {
    return this.min.unit
  }

  //----------------------------------------------------------------------------

  /**
   * A method for checking whether two ranges are exactly equal, not just
   * equivalent (i.e. (0 - 0 in) !== (0 - 0 ft))
   * @param a               The first range object, or undefined
   * @param b               The second range object, or undefined
   * @constructor
   */
  public static Equals(
    a: GeckoColorScaleThreshold | undefined,
    b: GeckoColorScaleThreshold | undefined
  ): boolean {
    if (a === b) return true
    if (!a) return !b
    if (!b) return !a
    return (
      GeckoMeasurement.Equals(a.min, b.min) &&
      GeckoMeasurement.Equals(a.mid, b.mid) &&
      GeckoMeasurement.Equals(a.max, b.max)
    )
  }

  /**
   * Factory method for creating a color threshold from a numbers and measurement unit
   * @param min The numeric value of the min
   * @param mid The numeric value of the mid (can be undefined)
   * @param max The numeric value of the max
   * @param measurementUnit The measurement unit of the scale
   * @constructor
   */
  public static CreateFromValues(
    min: number,
    mid: number,
    max: number,
    measurementUnit: GeckoMeasurementUnit
  ) {
    return new GeckoColorScaleThreshold(
      GeckoMeasurement.CreateFromValue(min, measurementUnit),
      GeckoMeasurement.CreateFromValue(mid, measurementUnit),
      GeckoMeasurement.CreateFromValue(max, measurementUnit)
    )
  }

  /**
   * Factory method for creating a color threshold from three measurements
   * @param min The numeric value of the min
   * @param mid The numeric value of the mid (can be undefined)
   * @param max The numeric value of the max
   * @constructor
   */
  public static CreateFromMeasurements(
    min: GeckoMeasurement,
    mid: GeckoMeasurement,
    max: GeckoMeasurement
  ) {
    return new GeckoColorScaleThreshold(min, mid, max)
  }

  //----------------------------------------------------------------------------

  /**
   * A method which returns a threshold object with new measurement units.
   * Note that if no conversion is necessary, this instance will be returned.
   * @param newUnit         The new measurement unit of this value range
   */
  public convertTo(
    newUnit: GeckoMeasurementUnit | undefined
  ): GeckoColorScaleThreshold {
    if (this.unit === newUnit || !newUnit) return this
    return GeckoColorScaleThreshold.CreateFromValues(
      this.min.convertTo(newUnit).num,
      this.mid.convertTo(newUnit).num,
      this.max.convertTo(newUnit).num,
      newUnit
    )
  }

  /**
   * A method returning a legible string form of this object's data
   */
  public toString(): string {
    return `${this.min.num} / ${this.mid.num}  / ${this.max.num} ${this.unit.id}`
  }
}
